/**
 * `AbilityTooltipAnchor` (#924) — the ONE hover anchor every
 * {@link AbilityTooltip} surface renders through.
 *
 * WHY THIS EXISTS (the invariant, previously prose-only):
 * `AbilityTooltip` is `position: absolute` and deliberately paints OUTSIDE
 * its containing box (`bottom: calc(100% + 8px)` above the anchor, or
 * `top: calc(100% + 8px)` below it). So it needs TWO separate boxes:
 *
 *   1. a purely-presentational `position: relative` ANCHOR (this component)
 *      — the positioning ancestor, which must never set `overflow`;
 *   2. the caller's own visual cell/slot/label (`children`), which MAY set
 *      `overflow: hidden` to clip its icon art, because the tooltip is no
 *      longer inside it.
 *
 * Collapsing those two boxes into one is exactly the #924 defect: #799 put
 * the tooltip directly inside `LoadoutPicker`'s 48×48 `overflow: hidden`
 * slot-layout cell. The tooltip was in the DOM, correctly styled, and
 * clipped to a measured 0px² of visible area — invisible to the player and
 * (jsdom implements no layout) unobservable to every unit test that covered
 * it. `ActionBar` had already hit and solved this once with a hand-rolled
 * wrapper; `LoadoutOption` had a second copy of the same wrapper. Three
 * copies of one invariant is three chances to break it, so it lives here
 * now, once.
 *
 * ⚠️ CALLER CONTRACT: do NOT wrap `children` in another clipping box and
 * expect the tooltip to escape it. The tooltip escapes THIS anchor's bounds
 * only; any `overflow: hidden` ancestor ABOVE the anchor clips it just the
 * same. If a caller needs a clipping container, the anchor goes INSIDE it
 * and the container must be large enough for the tooltip, or the tooltip
 * placement must be flipped.
 *
 * WHAT THIS ANCHOR DOES NOT DO (#924 scope 1a/1b):
 *   • It contributes NO box styling beyond `position: relative` — no
 *     padding/margin/border/background/size/overflow. All three call sites
 *     sit in a flex row or a CSS grid, so the anchor becomes the flex/grid
 *     item; anything opinionated here would shift those layouts.
 *   • It absorbs NO behavior from the cell it wraps. `data-testid`,
 *     `data-*` state attributes, `draggable` and the drag/drop handlers all
 *     stay on the caller's own element — moving them would change the
 *     testid target, the drag hit area and the drag-image source.
 *
 * `name === null` means "nothing to describe" (an empty/reserved slot) and
 * never shows a tooltip. `suppressed` is the caller's LOCAL veto for the
 * genuinely per-site cases: `LoadoutPicker`'s slot-layout cell suppresses
 * while it is the active drop target (the dashed drop border is what the
 * player needs mid-drag), `ActionBar` suppresses on an empty slot.
 *
 * PURELY COSMETIC: local hover state only, never the store/sim/hash.
 *
 * SCOPE NOTE (#924): only the ABILITY tooltip family routes through here.
 * `ui/AuraTooltip.tsx` (via `ui/AuraChip.tsx`) is a separate family with its
 * own right-flush placement semantics — it already implements this same
 * wrapper shape correctly by hand, so it is a follow-up DEDUPE candidate, not
 * a defect, and deliberately not converted here.
 */

import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import type { AbilityFacts } from "./abilityFacts.js";
import { AbilityTooltip, type AbilityTooltipPlacement } from "./AbilityTooltip.js";

/** The anchor's ENTIRE contribution to layout. Deliberately just the
 *  positioning ancestor — see the "layout-transparent" note above. */
const ANCHOR_STYLE: CSSProperties = { position: "relative" };

export interface AbilityTooltipAnchorProps {
  /** Forwarded verbatim to `AbilityTooltip`'s `data-testid`. */
  readonly testId: string;
  /** Ability display name, or `null` for "nothing to describe" — an empty or
   *  reserved slot, which never shows a tooltip. */
  readonly name: string | null;
  readonly description: string | null;
  readonly facts?: AbilityFacts | null;
  readonly placement?: AbilityTooltipPlacement;
  /** Caller-side veto for a per-site gate the shared rule can't know about
   *  (mid-drag drop target, empty action-bar slot). */
  readonly suppressed?: boolean;
  /** The caller's own visual cell/slot/label. MAY set its own `overflow`. */
  readonly children: ReactNode;
}

export function AbilityTooltipAnchor({
  testId,
  name,
  description,
  facts = null,
  placement = "above",
  suppressed = false,
  children,
}: AbilityTooltipAnchorProps): ReactElement {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={ANCHOR_STYLE}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* SIBLING BEFORE `children`, never inside them — the whole point of
          this component. Rendering order also matches what all three call
          sites did by hand, so nothing about paint order changes. */}
      {hovered && !suppressed && name !== null ? (
        <AbilityTooltip
          testId={testId}
          name={name}
          description={description}
          facts={facts}
          placement={placement}
        />
      ) : null}
      {children}
    </div>
  );
}
